/**
 * Contact Picker API (agenda do dispositivo) — suporte principalmente Android/Chrome.
 * https://developer.chrome.com/docs/capabilities/web-apis/contact-picker
 */

import { normalizePhoneDigits } from "@/lib/format-phone";
import type { FabContactCanonical } from "@/lib/whatsapp-contact-message";

export type DeviceContactProperty = "name" | "email" | "tel" | "address" | "icon";

export interface DevicePickedContact {
  name?: string[];
  email?: string[];
  tel?: string[];
}

declare global {
  interface Navigator {
    contacts?: {
      select(
        properties: DeviceContactProperty[],
        options?: { multiple?: boolean },
      ): Promise<DevicePickedContact[]>;
      getProperties?(): Promise<DeviceContactProperty[]>;
    };
  }
  interface Window {
    ContactsManager?: unknown;
  }
}

/** Agenda nativa via Contact Picker API (Chrome Android, alguns desktops Chromium). */
export function isDeviceContactPickerSupported(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  return "contacts" in navigator && typeof navigator.contacts?.select === "function";
}

function deviceContactToCanonical(contact: DevicePickedContact): FabContactCanonical | null {
  const name = (contact.name?.[0] || "").trim() || "Contato";
  const phones = [...new Set(
    (contact.tel || [])
      .map((tel) => normalizePhoneDigits(tel))
      .filter(Boolean),
  )];
  if (phones.length === 0) return null;

  const emails = (contact.email || [])
    .map((e) => String(e || "").trim())
    .filter(Boolean)
    .slice(0, 3);

  return {
    name,
    phones,
    ...(emails.length > 0 ? { emails } : {}),
  };
}

/**
 * Abre o seletor de contatos do sistema. Requer gesto do usuário (click).
 * Retorna null se cancelado ou sem telefone.
 */
export async function pickDeviceContact(): Promise<FabContactCanonical | null> {
  if (!isDeviceContactPickerSupported() || !navigator.contacts?.select) {
    throw new Error("UNSUPPORTED");
  }

  const properties: DeviceContactProperty[] = ["name", "tel", "email"];
  try {
    const available = await navigator.contacts.getProperties?.();
    const props = available
      ? properties.filter((p) => available.includes(p))
      : properties;
    const contacts = await navigator.contacts.select(props.length > 0 ? props : ["name", "tel"], {
      multiple: false,
    });
    if (!contacts?.length) return null;
    return deviceContactToCanonical(contacts[0]);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return null;
    throw err;
  }
}
